import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Fragment, type ReactElement, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { LoginRequired } from '@/components/LoginRequired';
import { FixedPageDocument } from '@/graphql/contentful/api';
import { useIsMdUp } from '@/hooks/useMedia';
import { useMyWatches } from '@/hooks/useMyWatches';
import { useNewsLetter } from '@/hooks/useNewsLetter';
import { useUser } from '@/hooks/useUser';
import { useWishList } from '@/hooks/useWishlist';
import {
  type ContentfulFixedPageCMSData,
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { AccountLayout } from '@/layouts/AccountLayout';
import { myAccountMessages } from '@/messages/myAccount';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkIsEligibleForWarrantyCampaign } from '@/views/MyWatchView/components/DesktopPreview';

import { Notice } from '../ShoppingBagView/components/ShoppingBagCard/ShoppingBagCard.styles';
import * as S from './MyAccountView.styles';

interface MyAccountViewProps {
  cms?: ContentfulFixedPageCMSData;
}
export const MyAccountView = ({ cms }: MyAccountViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const isMdUp = useIsMdUp();
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  const { country } = useRegionProvider();
  const { mergeWishlist } = useWishList({ countryCode: country.code });
  const { getNewsLetterSubscriptionStatus } = useNewsLetter();
  const [showNeedOptInNotice, setShowNeedOptInNotice] =
    useState<boolean>(false);
  const { getWatchesList } = useMyWatches();
  const {
    warrantyExtensionCampaignEnable,
    warrantyExtensionCampaignEndDate,
    warrantyExtensionCampaignStartDate,
  } = useConfigProvider();
  const initData = async () => {
    await mergeWishlist();
    if (!warrantyExtensionCampaignEnable) {
      return;
    }
    Promise.all([getNewsLetterSubscriptionStatus(), getWatchesList()]).then(
      ([newsLetterSubscriptionStatusRes, watchesListRes]) => {
        let showNotice = false;
        if (newsLetterSubscriptionStatusRes?.data) {
          const subscribedNewsletter =
            newsLetterSubscriptionStatusRes?.data?.activeSubscription;
          if (
            Array.isArray(watchesListRes?.data) &&
            watchesListRes.data.length
          ) {
            let hasEligibleLinkedWatches = false;

            hasEligibleLinkedWatches = watchesListRes?.data?.some((value) =>
              checkIsEligibleForWarrantyCampaign(
                value.is_extendable,
                warrantyExtensionCampaignEnable,
                warrantyExtensionCampaignStartDate,
                warrantyExtensionCampaignEndDate,
                value.start_date,
              ),
            );
            showNotice = !subscribedNewsletter && hasEligibleLinkedWatches;
          }
          setShowNeedOptInNotice(showNotice);
        }
      },
    );
  };
  useEffect(() => {
    initData();
  }, []);
  const { formatMessage } = useIntl();
  return (
    <S.StyledContainer
      data-test-id='MyAccountViewStyledContainer'
      isDesktop={isMdUp}
    >
      {showNeedOptInNotice && (
        <Notice textAlign='center'>
          {formatMessage(myAccountMessages.rememberToOptIn)}
        </Notice>
      )}
      {liveCms?.topContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(
            data,
            undefined,
            liveCms.cmsExtraData,
            true,
            isAuthenticated,
          )}
        </Fragment>
      ))}
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(
            data,
            undefined,
            liveCms.cmsExtraData,
            true,
            isAuthenticated,
          )}
        </Fragment>
      ))}
    </S.StyledContainer>
  );
};

MyAccountView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <AccountLayout
      backgroundColor='lightGrey'
      cmsData={page.props.cmsDefaultLayoutData}
      data-test-id='MyAccountViewAccountLayout'
    >
      <LoginRequired data-test-id='MyAccountViewLoginRequired'>
        {page}
      </LoginRequired>
    </AccountLayout>
  </DraftModeContext.Provider>
);
